import './styles/marketing.css';
import { formatGBP, lifetimePrice, MONTHLY_GBP, yearlyPerMonth, type PaidPlan } from './pricing';
import { initErrorReporter } from './error-reporter';

const prefersReducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ── Theme: light/dark follows the OS `prefers-color-scheme` automatically (the
   shared token stylesheet does the rest); the site carries no manual toggle. ── */

/* ── Accessible mobile nav (disclosure) ── */
function initMobileNav(): void {
  const btn = document.getElementById('nav-menu-btn');
  const menu = document.getElementById('mobile-nav');
  if (!(btn instanceof HTMLButtonElement) || !menu) return;

  const close = (): void => {
    menu.hidden = true;
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-label', 'Open menu');
  };
  const open = (): void => {
    menu.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    btn.setAttribute('aria-label', 'Close menu');
  };
  btn.addEventListener('click', () => (menu.hidden ? open() : close()));
  // A tap on any link, or Escape, closes the menu.
  menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !menu.hidden) {
      close();
      btn.focus();
    }
  });
}

/* ── Pricing: monthly ⇄ yearly. Yearly per-month + billed line are derived from
   the documented web rules in pricing.ts. The footnote shows the real lifetime
   figure (2.5× yearly) instead of a vague "available". ── */
function setPlan(plan: PaidPlan, period: 'monthly' | 'yearly'): void {
  const priceEl = document.querySelector(`[data-price="${plan}"]`);
  const billedEl = document.querySelector(`[data-billed="${plan}"]`);
  if (!priceEl || !billedEl) return;

  if (period === 'monthly') {
    priceEl.innerHTML = `${formatGBP(MONTHLY_GBP[plan])}<small>/mo</small>`;
    billedEl.textContent = 'Billed monthly';
  } else {
    priceEl.innerHTML = `${formatGBP(yearlyPerMonth(plan))}<small>/mo</small>`;
    billedEl.textContent = `${formatGBP(yearlyPerMonth(plan) * 12)} billed yearly`;
  }
}

function initPricing(): void {
  const monthlyBtn = document.getElementById('bill-monthly');
  const yearlyBtn = document.getElementById('bill-yearly');
  const foot = document.getElementById('price-foot');
  if (!(monthlyBtn instanceof HTMLButtonElement) || !(yearlyBtn instanceof HTMLButtonElement))
    return;

  const apply = (period: 'monthly' | 'yearly'): void => {
    setPlan('pro', period);
    setPlan('business', period);
    monthlyBtn.classList.toggle('on', period === 'monthly');
    yearlyBtn.classList.toggle('on', period === 'yearly');
    monthlyBtn.setAttribute('aria-pressed', String(period === 'monthly'));
    yearlyBtn.setAttribute('aria-pressed', String(period === 'yearly'));
  };

  monthlyBtn.addEventListener('click', () => apply('monthly'));
  yearlyBtn.addEventListener('click', () => apply('yearly'));

  if (foot) {
    foot.innerHTML =
      `Yearly plans bill once a year · A one-time <strong>Lifetime</strong> option is also available ` +
      `(Pro ${formatGBP(lifetimePrice('pro'))} · Business ${formatGBP(lifetimePrice('business'))}) · ` +
      `Prices in GBP.`;
  }
}

/* ── Carousel (decorative app-screen showcase). Slides are stacked in one grid
   cell (see .carousel-viewport) and transitioned with a slide + fade: the active
   slide animates to translateX(0)/opacity 1 while the others park left/right and
   fade out. Auto-advances on a timer (looping), pausing on hover/focus and when
   the tab is hidden, and is disabled under prefers-reduced-motion. The arrows +
   dots add pointer control; the arrows stay bounded (prev disabled on the first
   slide, next on the last) while the auto-advance loops. The whole region is
   aria-hidden and the controls are tabindex=-1 — eye-candy only; the headline /
   checklist carry the real information for keyboard and assistive-tech users. ── */
const CAROUSEL_OFFSET = 28; // px each slide parks left/right of centre
const CAROUSEL_INTERVAL = 5000; // ms between auto-advances

function setupCarousel(root: HTMLElement): void {
  const slides = Array.from(root.querySelectorAll<HTMLElement>('[data-carousel-slide]'));
  const dotsWrap = root.querySelector<HTMLElement>('[data-carousel-dots]');
  const prevBtn = root.querySelector<HTMLButtonElement>('[data-carousel-prev]');
  const nextBtn = root.querySelector<HTMLButtonElement>('[data-carousel-next]');
  if (slides.length < 2) return;

  let index = 0;

  const dots = slides.map((_, i) => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.tabIndex = -1;
    dot.className = 'carousel-dot';
    dot.setAttribute('aria-hidden', 'true');
    dot.addEventListener('click', () => {
      go(i);
      restart();
    });
    dotsWrap?.appendChild(dot);
    return dot;
  });

  function render(): void {
    slides.forEach((slide, i) => {
      const active = i === index;
      slide.classList.toggle('is-active', active);
      slide.style.opacity = active ? '1' : '0';
      slide.style.transform = active
        ? 'translateX(0)'
        : `translateX(${i < index ? -CAROUSEL_OFFSET : CAROUSEL_OFFSET}px)`;
    });
    dots.forEach((dot, i) => dot.classList.toggle('on', i === index));
    if (prevBtn) prevBtn.disabled = index === 0;
    if (nextBtn) nextBtn.disabled = index === slides.length - 1;
  }

  // wrap=true loops past the ends (used by auto-advance); the arrows clamp.
  function go(target: number, wrap = false): void {
    const n = slides.length;
    index = wrap ? (target + n) % n : Math.max(0, Math.min(n - 1, target));
    render();
  }

  let timer = 0;
  function stop(): void {
    if (timer) {
      clearInterval(timer);
      timer = 0;
    }
  }
  function restart(): void {
    stop();
    if (prefersReducedMotion) return;
    timer = window.setInterval(() => go(index + 1, true), CAROUSEL_INTERVAL);
  }

  prevBtn?.addEventListener('click', () => {
    go(index - 1);
    restart();
  });
  nextBtn?.addEventListener('click', () => {
    go(index + 1);
    restart();
  });

  // Pause while the pointer is over the carousel or it has focus, and when the
  // tab is backgrounded; resume otherwise.
  root.addEventListener('pointerenter', stop);
  root.addEventListener('pointerleave', restart);
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', restart);
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : restart()));

  render();
  restart();
}

function initCarousels(): void {
  document.querySelectorAll<HTMLElement>('[data-carousel]').forEach(setupCarousel);
}

/* ── Scroll reveal (skipped under reduced-motion) ── */
function initReveal(): void {
  const els = document.querySelectorAll('.reveal');
  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('in'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12 },
  );
  els.forEach((el) => io.observe(el));
}

initErrorReporter();
initMobileNav();
initPricing();
initCarousels();
initReveal();
